import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { PatientPortalAPI } from '../Services/API/patient-portal-api';
import { PortalSessionDataService, SESSION_KEYS } from '../Shared/utilities';
import { AuthBootstrapService } from '../Services/Authorization/auth-bootstrap.service';

/**
 * Stateless URL-token validation. Every entry into a token-protected route
 * (portalLogin, patientPayments, bookAppointment, patientReferral) hits the
 * validation API fresh — no shortcuts based on cached HOSPITAL_CONTEXT, no
 * "trust the previous session" path. If the URL token is valid, components
 * get their data via sessionStorage signals as before; if not, /noAccess.
 *
 * Storage written by this guard (HOSPITAL_CONTEXT, IS_QR_LANDING, LOGOUT_ITEM)
 * is read by components, NOT by the guard itself. There is no path where a
 * stale signal can substitute for live token validation.
 */
export const idCheckingGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const patientPortalApi = inject(PatientPortalAPI);
  const portalContextService = inject(PortalSessionDataService);
  const authBootstrap = inject(AuthBootstrapService);

  const token    = route.queryParamMap.get('eh');
  const orgCode  = route.paramMap.get('OrgCode');
  const branchId = route.paramMap.get('BranchId');

  // ─── Helper: fetch hospital data by OrgCode and store everything ──────────
  const fetchAndStoreHospitalData = (orgCodeVal: string, branchIdVal: string, isQrLanding: boolean, tokenContextData?: any) => {
    return patientPortalApi.GetDoctorsByOrganizationCode(orgCodeVal).pipe(
      map((hospitalResp: any) => {
        const hospitalData = {
          ...hospitalResp,
          hospitalId: hospitalResp.HospitalId ?? hospitalResp.OrganizationId ?? hospitalResp.ParentOrganizationId,
          branchId: branchIdVal,
          ...(tokenContextData ?? {})
        };
        portalContextService.set(SESSION_KEYS.HOSPITAL_CONTEXT, hospitalData);
        portalContextService.set(SESSION_KEYS.IS_QR_LANDING, isQrLanding);
        return true;
      }),
      catchError(err => {
        console.error('GetDoctorsByOrganizationCode failed', err);
        router.navigate(['/noAccess']);
        return of(false);
      })
    );
  };

  // ─── PATH 1: Token-based login ────────────────────────────────────────────
  // Stateless: ALWAYS validate the URL token via the API. The auth-bootstrap
  // wait + the retry interceptor handle cold-start scenarios; if those exhaust
  // and validation still fails, /noAccess is the correct outcome.
  if (token) {
    portalContextService.set(SESSION_KEYS.LOGOUT_ITEM, token);

    return from(authBootstrap.ensureToken()).pipe(
      switchMap(() => patientPortalApi.GetValidatePatientPortalLoginToken(token)),
      switchMap((resp: any) => {
        if (resp?.Status !== 'Success') {
          router.navigate(['/noAccess']);
          return of(false);
        }

        const tokenOrgCode  = resp.SubOrgCode ?? null;
        const tokenBranchId = resp.HospitalId ?? 0;

        if (tokenOrgCode) {
          return fetchAndStoreHospitalData(tokenOrgCode, tokenBranchId, true, resp);
        }

        portalContextService.set(SESSION_KEYS.HOSPITAL_CONTEXT, {
          ...resp,
          hospitalId: resp.HospitalId ?? resp.OrganizationId ?? resp.ParentOrganizationId
        });
        portalContextService.set(SESSION_KEYS.IS_QR_LANDING, true);
        return of(true);
      }),
      catchError(err => {
        console.error('Token validation failed', err);
        router.navigate(['/noAccess']);
        return of(false);
      })
    );
  }

  // ─── PATH 2: OrgCode + BranchId route params ─────────────────────────────
  if (orgCode && branchId) {
    return from(authBootstrap.ensureToken()).pipe(
      switchMap(() => fetchAndStoreHospitalData(orgCode, branchId, false))
    );
  }

  // ─── No recognised params ─────────────────────────────────────────────────
  router.navigate(['/noAccess']);
  return false;
};
